import { type ColumnDef } from "@tanstack/react-table";
import type { ApiTheme } from "../types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowUpDown,
  CircleEllipsis,
  PenBox,
  ToggleLeft,
  ToggleRight,
  History,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { TFunction } from "i18next";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/utils/date";
import { NavLink } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import DeleteButton from "@/components/delete-button";
import { themesRepo } from "@/lib/repositories";
import { toast } from "sonner";
import { handleApiError } from "@/utils/errors";
import { catchError } from "@/lib/errors";
import { queryClient } from "@/lib/query-client";
import { Checkbox } from "@/components/ui/checkbox";
import SuccessRateCell from "@/components/success-rate-cell";
import SuccessRateSortableHeader from "@/components/success-rate-sortable-header";
import { useThemesFilterStore } from "../stores";

async function onArchive(id: string, t: TFunction) {
  if (!id) return;
  const [error] = await catchError(
    themesRepo.patch(id, { status: "inactive" }),
  );
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["themes"] });
  toast.success(t("messages.theme_archive_success"));
}

async function onUnarchive(id: string, t: TFunction) {
  if (!id) return;
  const [error] = await catchError(themesRepo.patch(id, { status: "active" }));
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["themes"] });
  toast.success(t("messages.theme_unarchive_success"));
}

async function onDelete(id: string, t: TFunction) {
  if (!id) return;
  const [error] = await catchError(themesRepo.delete(id));
  if (error) {
    toast.error(handleApiError(error).message);
    return;
  }
  await queryClient.invalidateQueries({ queryKey: ["themes"] });
  toast.success(t("messages.theme_delete_success"));
}

// eslint-disable-next-line react-refresh/only-export-components
function ThemeSuccessRateHeader({ t }: { t: TFunction }) {
  const ordering = useThemesFilterStore((s) => s.ordering);
  const setOrdering = useThemesFilterStore((s) => s.setOrdering);
  return (
    <SuccessRateSortableHeader
      ordering={ordering}
      onOrderingChange={setOrdering}
      label={t("keys.success_rate")}
    />
  );
}

export const themesColumns = (t: TFunction): ColumnDef<ApiTheme>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={t("keys.select_all")}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={t("keys.select_row")}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "reference",
    header: t("keys.id_reference"),
    cell: ({ row }) => {
      return (
        <NavLink to={ROUTES.THEME_DETAILS(row.original.id)} end>
          <span className="text-gray-400 hover:text-primary hover:underline">
            {row.getValue("reference")}
          </span>
        </NavLink>
      );
    },
  },
  {
    accessorKey: "name",
    header: t("modules.themes.keys.name"),
    cell: ({ row }) => {
      return (
        <span className="font-medium truncate inline-block max-w-56">
          {row.getValue("name")}
        </span>
      );
    },
  },
  {
    accessorKey: "color",
    header: t("modules.themes.keys.color"),
    cell: ({ row }) => {
      const color = row.getValue("color") as string;
      return (
        <div className="flex items-center gap-2">
          <span
            className="size-4 rounded-full border border-gray-200 shrink-0"
            style={{ backgroundColor: color }}
          />
          <span className="text-gray-500">{color}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-1 cursor-pointer"
        >
          {t("keys.status")}
          <ArrowUpDown
            className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
          />
        </button>
      );
    },
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      const isActive = status === "active";
      return (
        <Badge variant={isActive ? "success" : "destructive"}>
          {isActive ? t("keys.active") : t("keys.desactivated")}
        </Badge>
      );
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => {
      return (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="flex items-center gap-1 cursor-pointer"
        >
          {t("keys.creation_date")}
          <ArrowUpDown
            className={`ml-1 size-4 ${column.getIsSorted() ? "text-primary" : "opacity-50"}`}
          />
        </button>
      );
    },
    cell: ({ row }) => {
      return (
        <span className="text-gray-500">
          {formatDate(row.getValue("created_at"))}
        </span>
      );
    },
  },
  {
    id: "success_rate",
    enableSorting: false,
    header: () => <ThemeSuccessRateHeader t={t} />,
    cell: ({ row }) => <SuccessRateCell value={row.original.success_rate} />,
  },
  {
    id: "actions",
    header: t("keys.table.actions"),
    cell: ({ row }) => {
      const isActive = row.original.status === "active";
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <span className="sr-only">Ouvrir le menu</span>
              <CircleEllipsis className="h-10 w-10 rotate-90 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <NavLink to={ROUTES.THEME_DETAILS(row.original.id)} end>
                <PenBox />
                {t("keys.table.edit")}
              </NavLink>
            </DropdownMenuItem>
            {isActive ? (
              <DropdownMenuItem onClick={() => onArchive(row.original.id, t)}>
                <ToggleLeft />
                {t("keys.inactive_action")}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => onUnarchive(row.original.id, t)}>
                <ToggleRight />
                {t("keys.active_action")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <NavLink
                to={`${ROUTES.HISTORY}?object_pk=${row.original.id}&model_type=topic`}
                end
              >
                <History />
                {t("keys.table.view_history")}
              </NavLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <DeleteButton
                onClick={() => onDelete(row.original.id, t)}
                className="w-full justify-start"
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
